import React, { useState, useEffect, useRef } from 'react'
import { ColorField } from './ColorField'
import { FontPicker, CompactFontPicker } from './FontPicker'
import { THEMES } from '../../../../gift-cards-v2/data'
import VoucherCard from '../../../../gift-cards-v2/VoucherCard'
import '../../../../gift-cards-v2/GiftCardsV2.css'
import { loadDesignLibrary, designToContent, designsForVoucher } from './voucherDesigns'

// Every multi-line field grows with its text. A fixed-height textarea clips
// the copy the salon is writing (and on mobile there is no resize handle at
// all), so height tracks scrollHeight instead.
const AutoGrowTextarea = (props) => {
  const ref = useRef(null)
  const fit = () => {
    const el = ref.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight + 2}px`
  }
  useEffect(() => { fit() }, [props.value]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <textarea
      rows={2}
      {...props}
      ref={ref}
      onInput={(e) => { fit(); if (props.onInput) props.onInput(e) }}
      style={{ ...props.style, overflow: 'hidden', resize: 'none' }}
    />
  )
}

// One affordance for every image field: when an image exists the preview
// carries a small change chip in its corner (a full-width "Change Image" bar
// under every picture read as anything but a button); when empty it is a
// proper labelled button.
const changeChipStyle = {
  position: 'absolute',
  right: '8px',
  bottom: '8px',
  width: '32px',
  height: '32px',
  border: 'none',
  borderRadius: '8px',
  background: 'rgba(26, 26, 30, 0.62)',
  color: '#fff',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer'
}
const ImageIconSvg = ({ size = 15 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
)
const ImageField = ({ src, onPick, addLabel = 'Add image', height = 110, marginBottom = 16, changeLabel = 'Change image' }) => (
  src ? (
    <div style={{ position: 'relative', marginBottom: `${marginBottom}px` }}>
      <img src={src} alt="" style={{ width: '100%', height: `${height}px`, objectFit: 'cover', borderRadius: '10px', display: 'block' }} />
      <button type="button" onClick={onPick} aria-label={changeLabel} title={changeLabel} style={changeChipStyle}>
        <ImageIconSvg size={14} />
      </button>
    </div>
  ) : (
    <button type="button" onClick={onPick} style={imagePickerButtonStyle}>
      <ImageIconSvg />
      {addLabel}
    </button>
  )
)

// URL field with a one-tap fill for the destination salons want almost every
// time. Typing {{BookingLink}} by hand is not something anyone should need
// to know how to do.
const bookingChipStyle = {
  marginTop: '7px',
  padding: '6px 11px',
  border: '1px solid rgba(0, 126, 160, 0.35)',
  borderRadius: '100px',
  background: 'rgba(0, 126, 160, 0.06)',
  color: '#007EA0',
  fontSize: '12px',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit'
}
const UrlField = ({ label = 'Link URL', value, onChange }) => (
  <div style={{ marginBottom: '16px' }}>
    <label style={labelStyle}>{label}</label>
    <input
      type="text"
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      placeholder="https://…"
      style={inputStyle}
    />
    {value === '{{BookingLink}}' ? (
      <p style={{ fontSize: '11.5px', color: '#007EA0', margin: '7px 0 0', fontWeight: 600 }}>
        Goes to your booking page, personalised for each client.
      </p>
    ) : (
      <button type="button" onClick={() => onChange('{{BookingLink}}')} style={bookingChipStyle}>
        Use my booking link
      </button>
    )}
  </div>
)

// ─── Card design grid for the voucher block editor ───
// Shows the designs allocated to the picked voucher (its curated subset, or
// every available design) as real card thumbnails. Collapses past 4 designs.
const DESIGN_GRID_COLLAPSED = 4
const VoucherDesignGrid = ({ content, updateBlock }) => {
  const [lib, setLib] = useState(null)
  const [expanded, setExpanded] = useState(false)

  useEffect(() => {
    let alive = true
    loadDesignLibrary().then(l => { if (alive) setLib(l) })
    return () => { alive = false }
  }, [])

  const gridLabel = { display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '6px' }

  if (lib === null) {
    return (
      <div style={{ marginBottom: '16px' }}>
        <label style={gridLabel}>Card Design</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
          {[1, 2].map(i => (
            <div key={i} style={{ height: '86px', borderRadius: '10px', backgroundColor: '#F2F2F7' }} />
          ))}
        </div>
      </div>
    )
  }

  const designs = designsForVoucher(lib, content.designIds, content.coverDesignId)
  if (!designs.length) return null
  const shown = expanded ? designs : designs.slice(0, DESIGN_GRID_COLLAPSED)

  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={gridLabel}>
        Card Design
        {content.designIds && content.designIds.length > 0 && (
          <span style={{ color: '#9ca3af' }}> · {designs.length} allocated to this voucher</span>
        )}
      </label>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        {shown.map(d => {
          const active = content.designId === d.id
          return (
            <button
              key={d.id}
              type="button"
              onClick={() => updateBlock(designToContent(d))}
              title={d.label || d.id}
              style={{
                padding: '6px 6px 5px',
                borderRadius: '10px',
                border: 'none',
                cursor: 'pointer',
                background: '#fff',
                boxShadow: active ? '0 0 0 2px #007EA0' : '0 0 0 1px #ECECF0',
                textAlign: 'left',
                fontFamily: 'inherit'
              }}
            >
              <div className="voucher-v2 eb-card-frame eb-card-frame--thumb" style={{ pointerEvents: 'none' }}>
                <VoucherCard
                  voucher={{
                    name: 'Gift Card',
                    template: d.occasion || 'signature',
                    theme: d.theme,
                    value: Number(content.value) || 50,
                    promo: null,
                    denominations: [],
                    ...(d.isCustom && d.config ? { customDesign: d.config } : {})
                  }}
                  aesthetic={d.aesthetic}
                  salonName={content.salonName || 'Your Salon'}
                  currency={content.currency || '£'}
                  showPromo={false}
                />
              </div>
              <span style={{
                display: 'block',
                fontSize: '10.5px',
                fontWeight: '600',
                color: active ? '#007EA0' : '#4B4B52',
                marginTop: '5px',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {d.isCustom ? '★ ' : ''}{d.label || d.id}
              </span>
            </button>
          )
        })}
      </div>
      {designs.length > DESIGN_GRID_COLLAPSED && (
        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          style={{
            width: '100%',
            marginTop: '8px',
            padding: '9px',
            border: '1px solid #ECECF0',
            borderRadius: '8px',
            background: '#fff',
            fontSize: '12px',
            fontWeight: '600',
            color: '#4B4B52',
            cursor: 'pointer',
            fontFamily: 'inherit'
          }}
        >
          {expanded ? 'Show fewer designs' : `Show all ${designs.length} designs`}
        </button>
      )}
    </div>
  )
}

// Inline date + time picker for countdown blocks
const CountdownPicker = ({ value, onChange }) => {
  const today = new Date()
  const parsed = value ? new Date(value) : null
  const validParsed = parsed && !isNaN(parsed.getTime()) ? parsed : null

  const [calendarMonth, setCalendarMonth] = useState(validParsed ? validParsed.getMonth() : today.getMonth())
  const [calendarYear, setCalendarYear] = useState(validParsed ? validParsed.getFullYear() : today.getFullYear())
  const [selectedDate, setSelectedDate] = useState(validParsed ? value.split('T')[0] : '')
  const [selectedTime, setSelectedTime] = useState(
    validParsed ? `${String(validParsed.getHours()).padStart(2, '0')}:${String(validParsed.getMinutes()).padStart(2, '0')}` : ''
  )
  const [showCustomTime, setShowCustomTime] = useState(false)
  const [customHour, setCustomHour] = useState(validParsed ? String(validParsed.getHours()).padStart(2, '0') : '12')
  const [customMinute, setCustomMinute] = useState(validParsed ? String(validParsed.getMinutes()).padStart(2, '0') : '00')

  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  const dayNames = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su']

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate()
  const getFirstDayOfMonth = (month, year) => {
    const day = new Date(year, month, 1).getDay()
    return day === 0 ? 6 : day - 1
  }

  const daysInMonth = getDaysInMonth(calendarMonth, calendarYear)
  const firstDay = getFirstDayOfMonth(calendarMonth, calendarYear)

  const isDateDisabled = (day) => {
    const date = new Date(calendarYear, calendarMonth, day)
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate())
    return date < todayStart
  }

  const isDateSelected = (day) => {
    if (!selectedDate) return false
    const [y, m, d] = selectedDate.split('-').map(Number)
    return d === day && m - 1 === calendarMonth && y === calendarYear
  }

  const emitChange = (dateStr, timeStr) => {
    if (dateStr && timeStr) {
      onChange(`${dateStr}T${timeStr}`)
    }
  }

  const selectDate = (day) => {
    if (isDateDisabled(day)) return
    const dateStr = `${calendarYear}-${String(calendarMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
    setSelectedDate(dateStr)
    emitChange(dateStr, selectedTime)
  }

  const selectTime = (time) => {
    setSelectedTime(time)
    setShowCustomTime(false)
    emitChange(selectedDate, time)
  }

  const prevMonth = () => {
    if (calendarMonth === 0) { setCalendarMonth(11); setCalendarYear(calendarYear - 1) }
    else setCalendarMonth(calendarMonth - 1)
  }
  const nextMonth = () => {
    if (calendarMonth === 11) { setCalendarMonth(0); setCalendarYear(calendarYear + 1) }
    else setCalendarMonth(calendarMonth + 1)
  }

  const canGoPrev = calendarYear > today.getFullYear() || (calendarYear === today.getFullYear() && calendarMonth > today.getMonth())

  const isToday = (day) => day === today.getDate() && calendarMonth === today.getMonth() && calendarYear === today.getFullYear()

  const labelSt = { display: 'block', fontSize: '12px', color: '#6b7280', marginBottom: '6px' }

  return (
    <div>
      {/* Date Picker */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelSt}>End Date</label>
        <div style={{ backgroundColor: '#f9fafb', borderRadius: '10px', padding: '12px' }}>
          {/* Month nav */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
            <span style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a' }}>
              {monthNames[calendarMonth]} {calendarYear}
            </span>
            <div style={{ display: 'flex', gap: '4px' }}>
              <button
                onClick={prevMonth}
                disabled={!canGoPrev}
                style={{
                  width: '28px', height: '28px', border: 'none',
                  backgroundColor: canGoPrev ? '#e5e7eb' : 'transparent',
                  borderRadius: '6px', cursor: canGoPrev ? 'pointer' : 'default',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: canGoPrev ? 1 : 0.3
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
              </button>
              <button
                onClick={nextMonth}
                style={{
                  width: '28px', height: '28px', border: 'none', backgroundColor: '#e5e7eb',
                  borderRadius: '6px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
            </div>
          </div>

          {/* Day names */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', marginBottom: '4px' }}>
            {dayNames.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: '10px', fontWeight: '500', color: '#9ca3af', padding: '2px 0' }}>{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`e-${i}`} style={{ height: '30px' }} />
            ))}
            {Array.from({ length: daysInMonth }).map((_, i) => {
              const day = i + 1
              const disabled = isDateDisabled(day)
              const selected = isDateSelected(day)
              const todayDay = isToday(day)
              return (
                <button
                  key={day}
                  onClick={() => selectDate(day)}
                  disabled={disabled}
                  style={{
                    height: '30px', border: 'none', borderRadius: '6px',
                    backgroundColor: selected ? '#29282D' : 'transparent',
                    color: selected ? 'white' : disabled ? '#d4d4d4' : '#1a1a1a',
                    fontSize: '12px', fontWeight: todayDay && !selected ? '700' : '400',
                    cursor: disabled ? 'default' : 'pointer',
                    position: 'relative', transition: 'all 0.15s ease'
                  }}
                >
                  {day}
                  {todayDay && !selected && (
                    <div style={{
                      position: 'absolute', bottom: '2px', left: '50%', transform: 'translateX(-50%)',
                      width: '3px', height: '3px', borderRadius: '50%', backgroundColor: '#007EA0'
                    }} />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      </div>

      {/* Time Picker */}
      <div style={{ marginBottom: '16px' }}>
        <label style={labelSt}>End Time</label>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: showCustomTime ? '8px' : '0' }}>
          {['09:00', '12:00', '15:00', '18:00', '21:00'].map(time => (
            <button
              key={time}
              onClick={() => selectTime(time)}
              style={{
                padding: '8px 12px',
                backgroundColor: selectedTime === time && !showCustomTime ? '#29282D' : 'white',
                color: selectedTime === time && !showCustomTime ? 'white' : '#374151',
                border: `1px solid ${selectedTime === time && !showCustomTime ? '#29282D' : '#e5e5e5'}`,
                borderRadius: '8px', fontSize: '12px', fontWeight: '500',
                cursor: 'pointer', transition: 'all 0.15s ease'
              }}
            >
              {time}
            </button>
          ))}
          <button
            onClick={() => setShowCustomTime(!showCustomTime)}
            style={{
              padding: '8px 12px',
              backgroundColor: showCustomTime ? '#29282D' : 'white',
              color: showCustomTime ? 'white' : '#374151',
              border: `1px solid ${showCustomTime ? '#29282D' : '#e5e5e5'}`,
              borderRadius: '8px', fontSize: '12px', fontWeight: '500',
              cursor: 'pointer', transition: 'all 0.15s ease',
              display: 'flex', alignItems: 'center', gap: '4px'
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
            </svg>
            Custom
          </button>
        </div>

        {showCustomTime && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '6px',
            padding: '10px 12px', backgroundColor: '#f9fafb', borderRadius: '8px'
          }}>
            <select
              value={customHour}
              onChange={(e) => {
                setCustomHour(e.target.value)
                const time = `${e.target.value}:${customMinute}`
                setSelectedTime(time)
                emitChange(selectedDate, time)
              }}
              style={{
                padding: '8px 10px', border: '1px solid #e5e5e5', borderRadius: '6px',
                fontSize: '13px', fontWeight: '500', backgroundColor: 'white',
                cursor: 'pointer', outline: 'none', minWidth: '60px'
              }}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={String(i).padStart(2, '0')}>{String(i).padStart(2, '0')}</option>
              ))}
            </select>
            <span style={{ fontSize: '16px', fontWeight: '600', color: '#374151' }}>:</span>
            <select
              value={customMinute}
              onChange={(e) => {
                setCustomMinute(e.target.value)
                const time = `${customHour}:${e.target.value}`
                setSelectedTime(time)
                emitChange(selectedDate, time)
              }}
              style={{
                padding: '8px 10px', border: '1px solid #e5e5e5', borderRadius: '6px',
                fontSize: '13px', fontWeight: '500', backgroundColor: 'white',
                cursor: 'pointer', outline: 'none', minWidth: '60px'
              }}
            >
              {['00', '15', '30', '45'].map(min => (
                <option key={min} value={min}>{min}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Summary */}
      {selectedDate && selectedTime && (
        <div style={{
          padding: '10px 12px', backgroundColor: '#f0fdfa', borderRadius: '8px',
          marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#0d9488" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          <span style={{ fontSize: '13px', color: '#0d9488', fontWeight: '500' }}>
            Ends {new Date(selectedDate).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' })} at {selectedTime}
          </span>
        </div>
      )}
    </div>
  )
}

// Property editor panel for different block types
export const BlockEditor = ({ block, onUpdate, onDelete, onDuplicate, onOpenImagePicker, onOpenContentPicker }) => {
  const content = block.content || {}

  const updateBlock = (updates) => {
    onUpdate(block.id, updates)
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Block Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px',
        paddingBottom: '16px',
        borderBottom: '1px solid #e5e5e5'
      }}>
        <h3 style={{ fontSize: '14px', fontWeight: '600', color: '#1a1a1a', margin: 0, textTransform: 'capitalize' }}>
          {block.type.replace('layout-', '').replace(/-/g, ' ')} Block
        </h3>
        <div style={{ display: 'flex', gap: '4px' }}>
          {onDuplicate && (
            <button
              onClick={() => onDuplicate(block.id)}
              style={iconButtonStyle}
              title="Duplicate"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          )}
          {onDelete && block.type !== 'footer' && (
            <button
              onClick={() => onDelete(block.id)}
              style={{ ...iconButtonStyle, color: '#ef4444' }}
              title="Delete"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Text Block Editor */}
      {block.type === 'text' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Text Content</label>
            <AutoGrowTextarea
              value={content.text || ''}
              onChange={(e) => updateBlock({ text: e.target.value })}
              placeholder="Enter your text..."
              style={{
                ...inputStyle,
                minHeight: '120px',
                resize: 'vertical'
              }}
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Size</label>
              <select
                value={content.fontSize || '16'}
                onChange={(e) => updateBlock({ fontSize: e.target.value })}
                style={inputStyle}
              >
                {[12, 14, 16, 18, 20, 24, 28, 32, 36, 48].map(size => (
                  <option key={size} value={size}>{size}px</option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Weight</label>
              <button
                onClick={() => updateBlock({ bold: !content.bold })}
                style={{
                  ...inputStyle,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: content.bold ? '700' : '400',
                  backgroundColor: content.bold ? '#29282D' : 'white',
                  color: content.bold ? 'white' : '#374151'
                }}
              >
                Bold
              </button>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Alignment</label>
            <div style={{ display: 'flex', gap: '2px' }}>
              {['left', 'center', 'right'].map(align => (
                <button
                  key={align}
                  onClick={() => updateBlock({ align })}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: content.align === align ? '#29282D' : '#f3f4f6',
                    color: content.align === align ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: align === 'left' ? '8px 0 0 8px' : align === 'right' ? '0 8px 8px 0' : '0',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {align === 'center' ? 'centre' : align}
                </button>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Letter Spacing</label>
              <select
                value={content.letterSpacing || ''}
                onChange={(e) => updateBlock({ letterSpacing: e.target.value || null })}
                style={inputStyle}
              >
                <option value="">Normal</option>
                <option value="0.08em">Wide</option>
                <option value="0.18em">Extra Wide</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Case</label>
              <button
                onClick={() => updateBlock({ uppercase: !content.uppercase })}
                style={{
                  ...inputStyle,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  letterSpacing: '0.08em',
                  backgroundColor: content.uppercase ? '#29282D' : 'white',
                  color: content.uppercase ? 'white' : '#374151'
                }}
              >
                AA
              </button>
            </div>
          </div>

          <ColorField
            label="Text Colour"
            value={content.textColor || '#374151'}
            onChange={(color) => updateBlock({ textColor: color })}
          />

          <ColorField
            label="Background Colour"
            value={content.backgroundColor || ''}
            onChange={(color) => updateBlock({ backgroundColor: color })}
          />
        </>
      )}

      {/* Button Block Editor */}
      {block.type === 'button' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Button Text</label>
            <input
              type="text"
              value={content.text || ''}
              onChange={(e) => updateBlock({ text: e.target.value })}
              style={inputStyle}
            />
          </div>

          <UrlField value={content.url} onChange={(url) => updateBlock({ url })} />

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Style</label>
              <select
                value={content.variant || 'fill'}
                onChange={(e) => updateBlock({ variant: e.target.value })}
                style={inputStyle}
              >
                <option value="fill">Filled</option>
                <option value="outline">Outline</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Shape</label>
              <select
                value={content.shape || 'rounded'}
                onChange={(e) => updateBlock({ shape: e.target.value })}
                style={inputStyle}
              >
                <option value="rounded">Rounded</option>
                <option value="pill">Pill</option>
              </select>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Case</label>
              <button
                onClick={() => updateBlock({ uppercase: !content.uppercase })}
                style={{
                  ...inputStyle,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  letterSpacing: '0.08em',
                  backgroundColor: content.uppercase ? '#29282D' : 'white',
                  color: content.uppercase ? 'white' : '#374151'
                }}
              >
                UPPERCASE
              </button>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Size</label>
              <select
                value={String(content.fontSize || 14)}
                onChange={(e) => updateBlock({ fontSize: Number(e.target.value) })}
                style={inputStyle}
              >
                {[11, 12, 13, 14, 15, 16, 18].map(size => (
                  <option key={size} value={size}>{size}px</option>
                ))}
              </select>
            </div>
          </div>

          <ColorField
            label="Button Colour"
            value={content.buttonColor || '#29282D'}
            onChange={(color) => updateBlock({ buttonColor: color })}
          />

          <ColorField
            label="Text Colour"
            value={content.textColor || '#ffffff'}
            onChange={(color) => updateBlock({ textColor: color })}
          />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Alignment</label>
            <div style={{ display: 'flex', gap: '2px' }}>
              {['left', 'center', 'right'].map(align => (
                <button
                  key={align}
                  onClick={() => updateBlock({ align })}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: content.align === align ? '#29282D' : '#f3f4f6',
                    color: content.align === align ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: align === 'left' ? '8px 0 0 8px' : align === 'right' ? '0 8px 8px 0' : '0',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {align === 'center' ? 'centre' : align}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Image Block Editor */}
      {block.type === 'image' && (
        <>
          <ImageField src={content.url} height={120} onPick={() => onOpenImagePicker && onOpenImagePicker('block')} addLabel="Choose image" />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Alt Text</label>
            <input
              type="text"
              value={content.alt || ''}
              onChange={(e) => updateBlock({ alt: e.target.value })}
              placeholder="Describe the image"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Width</label>
            <div style={{ display: 'flex', gap: '2px' }}>
              {[
                { id: false, label: 'Padded' },
                { id: true, label: 'Full width' },
              ].map((opt, i) => (
                <button
                  key={opt.label}
                  onClick={() => updateBlock({ fullWidth: opt.id })}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: !!content.fullWidth === opt.id ? '#29282D' : '#f3f4f6',
                    color: !!content.fullWidth === opt.id ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: i === 0 ? '8px 0 0 8px' : '0 8px 8px 0',
                    fontSize: '12px',
                    cursor: 'pointer'
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <ColorField
            label="Background Colour"
            value={content.backgroundColor || ''}
            onChange={(color) => updateBlock({ backgroundColor: color })}
          />
        </>
      )}

      {/* Logo Block Editor */}
      {block.type === 'logo' && (
        <>
          {content.url ? (
            /* Logos keep their natural proportions, so this is a bespoke
               preview rather than ImageField's cover crop. */
            <div style={{ position: 'relative', marginBottom: '16px', textAlign: 'center', padding: '10px', border: '1px solid #ECECF0', borderRadius: '10px' }}>
              <img
                src={content.url}
                alt="Logo"
                style={{ maxWidth: `${content.maxWidth || 150}px`, height: 'auto' }}
              />
              <button type="button" onClick={() => onOpenImagePicker && onOpenImagePicker('block')} aria-label="Change logo" title="Change logo" style={changeChipStyle}>
                <ImageIconSvg size={14} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => onOpenImagePicker && onOpenImagePicker('block')}
              style={imagePickerButtonStyle}
            >
              <ImageIconSvg />
              Upload logo
            </button>
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Max Width (px)</label>
            <input
              type="number"
              value={content.maxWidth || '150'}
              onChange={(e) => updateBlock({ maxWidth: e.target.value })}
              min="50"
              max="400"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Alignment</label>
            <div style={{ display: 'flex', gap: '2px' }}>
              {['left', 'center', 'right'].map(align => (
                <button
                  key={align}
                  onClick={() => updateBlock({ align })}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: content.align === align ? '#29282D' : '#f3f4f6',
                    color: content.align === align ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: align === 'left' ? '8px 0 0 8px' : align === 'right' ? '0 8px 8px 0' : '0',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {align === 'center' ? 'centre' : align}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Spacer Block Editor */}
      {block.type === 'spacer' && (
        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Height (px)</label>
          <input
            type="range"
            min="10"
            max="100"
            value={content.height || '30'}
            onChange={(e) => updateBlock({ height: e.target.value })}
            style={{ width: '100%', marginBottom: '8px' }}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af' }}>
            <span>10px</span>
            <span>{content.height || 30}px</span>
            <span>100px</span>
          </div>
        </div>
      )}

      {/* Divider Block Editor */}
      {block.type === 'divider' && (
        <ColorField
          label="Line Colour"
          value={content.color || '#e5e5e5'}
          onChange={(color) => updateBlock({ color })}
        />
      )}

      {/* Review Block Editor */}
      {block.type === 'review' && (
        <>
          <div style={{
            fontSize: '11px', fontWeight: '600', color: '#007EA0', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: '12px'
          }}>Review Block</div>

          <div style={{
            padding: '16px', backgroundColor: '#faf9f7', borderRadius: '10px', marginBottom: '16px',
            textAlign: 'center'
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '2px', marginBottom: '8px' }}>
              {[1,2,3,4,5].map(i => (
                <svg key={i} width="14" height="14" viewBox="0 0 20 20" fill={i <= (content.rating || 5) ? '#f59e0b' : '#e5e7eb'}>
                  <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
              ))}
            </div>
            <p style={{
              fontSize: '12px', color: '#374151', fontStyle: 'italic', margin: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
            }}>
              "{content.text || 'Review text...'}"
            </p>
          </div>

          <button
            onClick={() => onOpenContentPicker && onOpenContentPicker('review')}
            style={contentPickerButtonStyle}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
            </svg>
            Choose from real reviews
          </button>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Review Text</label>
            <AutoGrowTextarea
              value={content.text || ''}
              onChange={(e) => updateBlock({ text: e.target.value })}
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Customer Name</label>
              <input
                type="text"
                value={content.author || ''}
                onChange={(e) => updateBlock({ author: e.target.value })}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Service</label>
            <input
              type="text"
              value={content.service || ''}
              onChange={(e) => updateBlock({ service: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Rating</label>
            <div style={{ display: 'flex', gap: '2px' }}>
              {[1,2,3,4,5].map(star => (
                <button
                  key={star}
                  onClick={() => updateBlock({ rating: star })}
                  style={{
                    background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                    transition: 'transform 0.1s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.2)'}
                  onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
                >
                  <svg width="24" height="24" viewBox="0 0 20 20" fill={star <= (content.rating || 5) ? '#f59e0b' : '#e5e7eb'}>
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                  </svg>
                </button>
              ))}
            </div>
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <ColorField
            label="Text Colour"
            value={content.textColor || '#374151'}
            onChange={(color) => updateBlock({ textColor: color })}
          />

          <ColorField
            label="Background Colour"
            value={content.backgroundColor || '#faf9f7'}
            onChange={(color) => updateBlock({ backgroundColor: color })}
          />

          <ColorField
            label="Section Background"
            value={content.sectionColor || ''}
            onChange={(color) => updateBlock({ sectionColor: color })}
          />
        </>
      )}

      {/* Service Block Editor */}
      {block.type === 'service' && (
        <>
          <div style={{
            fontSize: '11px', fontWeight: '600', color: '#007EA0', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: '12px'
          }}>Service Block</div>

          <ImageField src={content.image} height={120} onPick={() => onOpenImagePicker && onOpenImagePicker('block')} />

          <button
            onClick={() => onOpenContentPicker && onOpenContentPicker('service')}
            style={contentPickerButtonStyle}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
            </svg>
            Choose from service menu
          </button>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Service Name</label>
            <input
              type="text"
              value={content.name || ''}
              onChange={(e) => updateBlock({ name: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Description</label>
            <AutoGrowTextarea
              value={content.description || ''}
              onChange={(e) => updateBlock({ description: e.target.value })}
              style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Price (£)</label>
              <input
                type="text"
                value={content.price || ''}
                onChange={(e) => updateBlock({ price: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Duration</label>
              <input
                type="text"
                value={content.duration || ''}
                onChange={(e) => updateBlock({ duration: e.target.value })}
                placeholder="e.g., 60 min"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Layout</label>
            <select
              value={content.variant || 'card'}
              onChange={(e) => updateBlock({ variant: e.target.value })}
              style={inputStyle}
            >
              <option value="card">Card (image left)</option>
              <option value="stacked">Stacked (image top)</option>
              <option value="minimal">Minimal (menu row)</option>
            </select>
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <ColorField
            label="Text Colour"
            value={content.textColor || '#1a1a1a'}
            onChange={(color) => updateBlock({ textColor: color })}
          />

          <ColorField
            label="Price Colour"
            value={content.priceColor || '#007EA0'}
            onChange={(color) => updateBlock({ priceColor: color })}
          />

          <ColorField
            label="Card Colour"
            value={content.backgroundColor || '#fafafa'}
            onChange={(color) => updateBlock({ backgroundColor: color })}
          />

          <ColorField
            label="Section Background"
            value={content.sectionColor || ''}
            onChange={(color) => updateBlock({ sectionColor: color })}
          />
        </>
      )}

      {/* Product Block Editor */}
      {block.type === 'product' && (
        <>
          <ImageField src={content.image} height={100} onPick={() => onOpenImagePicker && onOpenImagePicker('image')} addLabel="Choose image" />

          <button
            onClick={() => onOpenContentPicker && onOpenContentPicker('product')}
            style={{ ...imagePickerButtonStyle, marginBottom: '20px' }}
          >
            Swap Product
          </button>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Layout</label>
            <select
              value={content.variant || 'card'}
              onChange={(e) => updateBlock({ variant: e.target.value })}
              style={inputStyle}
            >
              <option value="card">Card (image left)</option>
              <option value="stacked">Stacked (image top)</option>
              <option value="minimal">Minimal (menu row)</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Product Name</label>
            <input
              type="text"
              value={content.name || ''}
              onChange={(e) => updateBlock({ name: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Description</label>
            <AutoGrowTextarea
              value={content.description || ''}
              onChange={(e) => updateBlock({ description: e.target.value })}
              style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Price ({content.currency || '£'})</label>
              <input
                type="text"
                value={content.price || ''}
                onChange={(e) => updateBlock({ price: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Button Text</label>
              <input
                type="text"
                value={content.buttonText || ''}
                onChange={(e) => updateBlock({ buttonText: e.target.value })}
                placeholder="Shop now"
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Shop Link</label>
            <input
              type="text"
              value={content.url || ''}
              onChange={(e) => updateBlock({ url: e.target.value })}
              placeholder="Filled automatically when you pick a product"
              style={inputStyle}
            />
            <p style={{ fontSize: '11.5px', color: '#9ca3af', margin: '6px 0 0 2px', lineHeight: 1.4 }}>
              Links to this product on your online shop.
            </p>
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <ColorField
            label="Text Colour"
            value={content.textColor || '#1a1a1a'}
            onChange={(color) => updateBlock({ textColor: color })}
          />

          <ColorField
            label="Price Colour"
            value={content.priceColor || '#1a1a1a'}
            onChange={(color) => updateBlock({ priceColor: color })}
          />

          <ColorField
            label="Button Colour"
            value={content.buttonColor || '#29282D'}
            onChange={(color) => updateBlock({ buttonColor: color })}
          />

          <ColorField
            label="Button Text Colour"
            value={content.buttonTextColor || '#ffffff'}
            onChange={(color) => updateBlock({ buttonTextColor: color })}
          />

          <ColorField
            label="Card Colour"
            value={content.backgroundColor || '#fafafa'}
            onChange={(color) => updateBlock({ backgroundColor: color })}
          />

          <ColorField
            label="Section Background"
            value={content.sectionColor || ''}
            onChange={(color) => updateBlock({ sectionColor: color })}
          />
        </>
      )}

      {/* Voucher Block Editor — drives the real gift-card designer card */}
      {block.type === 'voucher' && (
        <>
          <button
            onClick={() => onOpenContentPicker && onOpenContentPicker('voucher')}
            style={{ ...imagePickerButtonStyle, marginBottom: '20px' }}
          >
            Swap Voucher
          </button>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Layout</label>
            <select
              value={content.variant || 'showcase'}
              onChange={(e) => updateBlock({ variant: e.target.value })}
              style={inputStyle}
            >
              <option value="showcase">Showcase (full card)</option>
              <option value="split">Split (card beside details)</option>
              <option value="minimal">Minimal (banner row)</option>
            </select>
          </div>

          {(content.variant || 'showcase') !== 'minimal' && (
            <VoucherDesignGrid content={content} updateBlock={updateBlock} />
          )}

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Theme</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '8px' }}>
              {THEMES.map(t => {
                const active = (content.theme || 'rose') === t.id
                return (
                  <button
                    key={t.id}
                    onClick={() => updateBlock({ theme: t.id })}
                    title={t.name}
                    aria-label={`${t.name} theme`}
                    style={{
                      width: '100%',
                      aspectRatio: '1',
                      borderRadius: '50%',
                      backgroundColor: t.bg,
                      border: '2px solid #fff',
                      boxShadow: active ? `0 0 0 2px #29282D` : '0 0 0 1px rgba(0,0,0,0.12)',
                      cursor: 'pointer',
                      padding: 0
                    }}
                  />
                )
              })}
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Voucher Name</label>
            <input
              type="text"
              value={content.name || ''}
              onChange={(e) => updateBlock({ name: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Card Value ({content.currency || '£'})</label>
              <input
                type="text"
                value={content.value || ''}
                onChange={(e) => updateBlock({ value: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>From Price ({content.currency || '£'})</label>
              <input
                type="text"
                value={content.fromPrice || ''}
                onChange={(e) => updateBlock({ fromPrice: e.target.value })}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Validity</label>
              <input
                type="text"
                value={content.expiry || ''}
                onChange={(e) => updateBlock({ expiry: e.target.value })}
                placeholder="e.g. 12 months"
                style={inputStyle}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Salon Name on Card</label>
              <input
                type="text"
                value={content.salonName || ''}
                onChange={(e) => updateBlock({ salonName: e.target.value })}
                style={inputStyle}
              />
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Button Text</label>
            <input
              type="text"
              value={content.buttonText || ''}
              onChange={(e) => updateBlock({ buttonText: e.target.value })}
              placeholder="Buy gift voucher"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Shop Link</label>
            <input
              type="text"
              value={content.url || ''}
              onChange={(e) => updateBlock({ url: e.target.value })}
              placeholder="Filled automatically when you pick a voucher"
              style={inputStyle}
            />
            <p style={{ fontSize: '11.5px', color: '#9ca3af', margin: '6px 0 0 2px', lineHeight: 1.4 }}>
              Links to this voucher in your gift card shop.
            </p>
          </div>

          <ColorField
            label="Button Colour"
            value={content.buttonColor || ''}
            onChange={(color) => updateBlock({ buttonColor: color })}
          />

          <ColorField
            label="Button Text Colour"
            value={content.buttonTextColor || ''}
            onChange={(color) => updateBlock({ buttonTextColor: color })}
          />

          <ColorField
            label="Section Background"
            value={content.sectionColor || ''}
            onChange={(color) => updateBlock({ sectionColor: color })}
          />
        </>
      )}

      {/* Team Block Editor */}
      {block.type === 'team' && (
        <>
          <div style={{
            fontSize: '11px', fontWeight: '600', color: '#007EA0', textTransform: 'uppercase',
            letterSpacing: '0.05em', marginBottom: '12px'
          }}>Team Block</div>

          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{
              width: '80px', height: '80px', borderRadius: '50%',
              margin: '0 auto 8px', padding: '3px',
              background: content.image ? 'linear-gradient(135deg, #007EA0 0%, #059669 100%)' : '#e5e7eb'
            }}>
              {content.image ? (
                <img
                  src={content.image}
                  alt="Team member"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', border: '2px solid white' }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%', borderRadius: '50%', backgroundColor: '#f9fafb',
                  border: '2px solid white',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#c4c4c4" strokeWidth="1.5">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                    <circle cx="12" cy="7" r="4"/>
                  </svg>
                </div>
              )}
            </div>
            {content.name && (
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#1a1a1a', margin: '0 0 1px 0' }}>{content.name}</p>
            )}
            {content.title && (
              <p style={{ fontSize: '11px', color: '#007EA0', margin: 0, fontWeight: '500' }}>{content.title}</p>
            )}
          </div>

          {content.image ? (
            /* The avatar preview above stays; this is just the change chip
               in button form, since a chip cannot sit on a circular crop. */
            <button type="button" onClick={() => onOpenImagePicker && onOpenImagePicker('block')} style={imagePickerButtonStyle}>
              <ImageIconSvg />
              Change photo
            </button>
          ) : (
            <button type="button" onClick={() => onOpenImagePicker && onOpenImagePicker('block')} style={imagePickerButtonStyle}>
              <ImageIconSvg />
              Add photo
            </button>
          )}

          <button
            onClick={() => onOpenContentPicker && onOpenContentPicker('team')}
            style={contentPickerButtonStyle}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
            Choose team member
          </button>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Name</label>
            <input
              type="text"
              value={content.name || ''}
              onChange={(e) => updateBlock({ name: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Title / Role</label>
            <input
              type="text"
              value={content.title || ''}
              onChange={(e) => updateBlock({ title: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Bio</label>
            <AutoGrowTextarea
              value={content.bio || ''}
              onChange={(e) => updateBlock({ bio: e.target.value })}
              style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <ColorField
            label="Text Colour"
            value={content.textColor || '#1a1a1a'}
            onChange={(color) => updateBlock({ textColor: color })}
          />

          <ColorField
            label="Background Colour"
            value={content.backgroundColor || ''}
            onChange={(color) => updateBlock({ backgroundColor: color })}
          />
        </>
      )}

      {/* Layout Gradient Editor */}
      {block.type === 'layout-gradient' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Headline</label>
            <input
              type="text"
              value={content.headline || ''}
              onChange={(e) => updateBlock({ headline: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Subtext</label>
            <input
              type="text"
              value={content.subtext || ''}
              onChange={(e) => updateBlock({ subtext: e.target.value })}
              style={inputStyle}
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Gradient Style</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              {['purple', 'teal', 'sunset', 'dark', 'warm'].map(g => (
                <button
                  key={g}
                  onClick={() => updateBlock({ gradient: g })}
                  style={{
                    padding: '10px',
                    backgroundColor: content.gradient === g ? '#29282D' : '#f3f4f6',
                    color: content.gradient === g ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <ColorField label="Text Colour" value={content.textColor || '#ffffff'} onChange={(color) => updateBlock({ textColor: color })} />
        </>
      )}

      {/* Layout Arch Editor */}
      {block.type === 'layout-arch' && (
        <>
          <ImageField src={content.imageUrl} height={100} onPick={() => onOpenImagePicker && onOpenImagePicker('block')} addLabel="Choose background" changeLabel="Change background" />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Headline</label>
            <input
              type="text"
              value={content.headline || ''}
              onChange={(e) => updateBlock({ headline: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Subtext</label>
            <input
              type="text"
              value={content.subtext || ''}
              onChange={(e) => updateBlock({ subtext: e.target.value })}
              style={inputStyle}
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <ColorField label="Text Colour" value={content.textColor || '#ffffff'} onChange={(color) => updateBlock({ textColor: color })} />
        </>
      )}

      {/* Layout Full Bleed Editor */}
      {block.type === 'layout-full-bleed' && (
        <>
          <ImageField src={content.imageUrl} height={100} onPick={() => onOpenImagePicker && onOpenImagePicker('block')} addLabel="Choose background image" changeLabel="Change image" />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Headline</label>
            <input
              type="text"
              value={content.headline || ''}
              onChange={(e) => updateBlock({ headline: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Subtext</label>
            <input
              type="text"
              value={content.subtext || ''}
              onChange={(e) => updateBlock({ subtext: e.target.value })}
              style={inputStyle}
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Overlay Opacity ({content.overlayOpacity || 40}%)</label>
            <input
              type="range"
              min="0"
              max="80"
              value={content.overlayOpacity || 40}
              onChange={(e) => updateBlock({ overlayOpacity: parseInt(e.target.value) })}
              style={{ width: '100%' }}
            />
          </div>

          <ColorField label="Text Colour" value={content.textColor || '#ffffff'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || '#1a1a1a'} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Layout Hero Split Editor */}
      {block.type === 'layout-hero-split' && (
        <>
          <ImageField src={content.imageUrl} height={100} onPick={() => onOpenImagePicker && onOpenImagePicker('block')} addLabel="Choose image" changeLabel="Change image" />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Headline</label>
            <input
              type="text"
              value={content.headline || ''}
              onChange={(e) => updateBlock({ headline: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Body Text</label>
            <AutoGrowTextarea
              value={content.body || ''}
              onChange={(e) => updateBlock({ body: e.target.value })}
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Image Position</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['left', 'right'].map(pos => (
                <button
                  key={pos}
                  onClick={() => updateBlock({ imagePosition: pos })}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: (content.imagePosition || 'left') === pos ? '#29282D' : '#f3f4f6',
                    color: (content.imagePosition || 'left') === pos ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: pos === 'left' ? '8px 0 0 8px' : '0 8px 8px 0',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {pos}
                </button>
              ))}
            </div>
          </div>

          <ColorField label="Text Colour" value={content.textColor || '#1a1a1a'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || '#ffffff'} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Layout Bento 2-Column Editor */}
      {block.type === 'layout-bento-2col' && (
        <>
          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
            Asymmetric grid: large image left, two stacked images right.
          </p>

          {[
            { field: 'mainImage', label: 'Main Image (Large Left)' },
            { field: 'topImage', label: 'Top Right Image' },
            { field: 'bottomImage', label: 'Bottom Right Image' },
          ].map(({ field, label }) => (
            <div key={field} style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>{label}</label>
              <ImageField src={content[field]} height={64} marginBottom={0} onPick={() => onOpenImagePicker && onOpenImagePicker(field)} addLabel="Choose image" />
            </div>
          ))}

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Overlay Text</label>
            <input
              type="text"
              value={content.mainText || ''}
              onChange={(e) => updateBlock({ mainText: e.target.value })}
              placeholder="Optional text on main image"
              style={inputStyle}
            />
          </div>
        </>
      )}

      {/* Layout Bento 3-Column Editor */}
      {block.type === 'layout-bento-3col' && (
        <>
          <p style={{ fontSize: '12px', color: '#6b7280', marginBottom: '16px' }}>
            Masonry grid: tall left, wide top-right, two small bottom-right.
          </p>

          {[
            { field: 'image1', label: 'Tall Left Image' },
            { field: 'image2', label: 'Wide Top Right Image' },
            { field: 'image3', label: 'Bottom Left Image' },
            { field: 'image4', label: 'Bottom Right Image' },
          ].map(({ field, label }, idx) => {
            const current = content[field] || (content.images && content.images[idx]) || ''
            return (
              <div key={field} style={{ marginBottom: '16px' }}>
                <label style={labelStyle}>{label}</label>
                <ImageField src={current} height={56} marginBottom={0} onPick={() => onOpenImagePicker && onOpenImagePicker(field)} addLabel="Choose image" />
              </div>
            )
          })}
        </>
      )}

      {/* Layout Feature Card Editor */}
      {block.type === 'layout-feature-card' && (
        <>
          <ImageField src={content.imageUrl} height={100} onPick={() => onOpenImagePicker && onOpenImagePicker('block')} addLabel="Choose image" changeLabel="Change image" />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Headline</label>
            <input
              type="text"
              value={content.headline || ''}
              onChange={(e) => updateBlock({ headline: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Body Text</label>
            <AutoGrowTextarea
              value={content.body || ''}
              onChange={(e) => updateBlock({ body: e.target.value })}
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Corner Radius ({content.cornerRadius || 16}px)</label>
            <input
              type="range"
              min="0"
              max="32"
              value={content.cornerRadius || 16}
              onChange={(e) => updateBlock({ cornerRadius: parseInt(e.target.value) })}
              style={{ width: '100%' }}
            />
          </div>

          <ColorField label="Text Colour" value={content.textColor || '#1a1a1a'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Card Background" value={content.backgroundColor || '#ffffff'} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Layout Glass Card Editor */}
      {block.type === 'layout-glass-card' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Headline</label>
            <input
              type="text"
              value={content.headline || ''}
              onChange={(e) => updateBlock({ headline: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Body Text</label>
            <AutoGrowTextarea
              value={content.body || ''}
              onChange={(e) => updateBlock({ body: e.target.value })}
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Gradient Style</label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px' }}>
              {['purple', 'teal', 'sunset', 'dark', 'warm'].map(g => (
                <button
                  key={g}
                  onClick={() => updateBlock({ gradient: g })}
                  style={{
                    padding: '10px',
                    backgroundColor: (content.gradient || 'purple') === g ? '#29282D' : '#f3f4f6',
                    color: (content.gradient || 'purple') === g ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>

          <ColorField label="Text Colour" value={content.textColor || '#ffffff'} onChange={(color) => updateBlock({ textColor: color })} />
        </>
      )}

      {/* Layout Rounded Image Editor */}
      {block.type === 'layout-rounded-image' && (
        <>
          <ImageField src={content.imageUrl} height={100} onPick={() => onOpenImagePicker && onOpenImagePicker('block')} addLabel="Choose image" changeLabel="Change image" />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Corner Radius ({content.cornerRadius || 24}px)</label>
            <input
              type="range"
              min="0"
              max="48"
              value={content.cornerRadius || 24}
              onChange={(e) => updateBlock({ cornerRadius: parseInt(e.target.value) })}
              style={{ width: '100%' }}
            />
          </div>
        </>
      )}

      {/* Layout Badge Editor */}
      {block.type === 'layout-badge' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Badge Text</label>
            <input
              type="text"
              value={content.badgeText || ''}
              onChange={(e) => updateBlock({ badgeText: e.target.value })}
              placeholder="e.g., NEW, SALE, LIMITED"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Headline</label>
            <input
              type="text"
              value={content.headline || ''}
              onChange={(e) => updateBlock({ headline: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Body Text</label>
            <AutoGrowTextarea
              value={content.body || ''}
              onChange={(e) => updateBlock({ body: e.target.value })}
              style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <ColorField label="Badge Colour" value={content.badgeColor || '#ef4444'} onChange={(color) => updateBlock({ badgeColor: color })} />
          <ColorField label="Text Colour" value={content.textColor || '#1a1a1a'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || '#ffffff'} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Layout Countdown Editor */}
      {block.type === 'layout-countdown' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Headline</label>
            <input
              type="text"
              value={content.headline || ''}
              onChange={(e) => updateBlock({ headline: e.target.value })}
              style={inputStyle}
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <CountdownPicker
            value={content.endDate || ''}
            onChange={(endDate) => updateBlock({ endDate })}
          />

          <ColorField label="Text Colour" value={content.textColor || '#ffffff'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || '#1a1a1a'} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Layout CTA Gradient Editor */}
      {block.type === 'layout-cta-gradient' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Eyebrow (optional)</label>
            <input
              type="text"
              value={content.eyebrow || ''}
              onChange={(e) => updateBlock({ eyebrow: e.target.value })}
              placeholder="e.g. TO CELEBRATE"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Headline</label>
            <input
              type="text"
              value={content.headline || ''}
              onChange={(e) => updateBlock({ headline: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Subtext (optional)</label>
            <input
              type="text"
              value={content.subtext || ''}
              onChange={(e) => updateBlock({ subtext: e.target.value })}
              style={inputStyle}
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <ColorField
            label="Background Colour"
            value={typeof content.background === 'string' && content.background.startsWith('#') ? content.background : '#29282D'}
            onChange={(color) => updateBlock({ background: color })}
          />

          <ColorField
            label="Headline Colour"
            value={content.headlineColor || '#ffffff'}
            onChange={(color) => updateBlock({ headlineColor: color })}
          />

          <ColorField
            label="Eyebrow Colour"
            value={content.eyebrowColor || ''}
            onChange={(color) => updateBlock({ eyebrowColor: color })}
          />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Button Text</label>
            <input
              type="text"
              value={content.buttonText || ''}
              onChange={(e) => updateBlock({ buttonText: e.target.value })}
              style={inputStyle}
            />
          </div>

          <UrlField label="Button URL" value={content.buttonUrl} onChange={(buttonUrl) => updateBlock({ buttonUrl })} />

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Button Style</label>
              <select
                value={content.buttonVariant || 'fill'}
                onChange={(e) => updateBlock({ buttonVariant: e.target.value })}
                style={inputStyle}
              >
                <option value="fill">Filled</option>
                <option value="outline">Outline</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Button Shape</label>
              <select
                value={content.buttonShape || 'rounded'}
                onChange={(e) => updateBlock({ buttonShape: e.target.value })}
                style={inputStyle}
              >
                <option value="rounded">Rounded</option>
                <option value="pill">Pill</option>
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Button Case</label>
            <button
              onClick={() => updateBlock({ buttonUppercase: !content.buttonUppercase })}
              style={{
                ...inputStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                letterSpacing: '0.08em',
                backgroundColor: content.buttonUppercase ? '#29282D' : 'white',
                color: content.buttonUppercase ? 'white' : '#374151'
              }}
            >
              UPPERCASE
            </button>
          </div>

          <ColorField
            label="Button Colour"
            value={content.buttonColor || '#ffffff'}
            onChange={(color) => updateBlock({ buttonColor: color })}
          />

          <ColorField
            label="Button Text Colour"
            value={content.buttonTextColor || '#007EA0'}
            onChange={(color) => updateBlock({ buttonTextColor: color })}
          />
        </>
      )}

      {/* Layout Promo Banner Editor */}
      {block.type === 'layout-promo-banner' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Promo Text</label>
            <input
              type="text"
              value={content.promoText || ''}
              onChange={(e) => updateBlock({ promoText: e.target.value })}
              placeholder="e.g., 20% OFF"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Subtext</label>
            <input
              type="text"
              value={content.subtext || ''}
              onChange={(e) => updateBlock({ subtext: e.target.value })}
              placeholder="e.g., Use code SAVE20"
              style={inputStyle}
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <ColorField label="Text Colour" value={content.promoTextColor || '#1a1a1a'} onChange={(color) => updateBlock({ promoTextColor: color })} />
          <ColorField label="Subtext Colour" value={content.subtextColor || '#374151'} onChange={(color) => updateBlock({ subtextColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || '#fbbf24'} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Layout Before/After Editor */}
      {block.type === 'layout-before-after' && (
        <>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '4px' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={labelStyle}>Before</label>
              <ImageField src={content.beforeImage} height={64} marginBottom={0} onPick={() => onOpenImagePicker && onOpenImagePicker('beforeImage')} addLabel="Add" changeLabel="Change before image" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <label style={labelStyle}>After</label>
              <ImageField src={content.afterImage} height={64} marginBottom={0} onPick={() => onOpenImagePicker && onOpenImagePicker('afterImage')} addLabel="Add" changeLabel="Change after image" />
            </div>
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Before Label</label>
              <input type="text" value={content.beforeLabel || ''} onChange={(e) => updateBlock({ beforeLabel: e.target.value })} style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>After Label</label>
              <input type="text" value={content.afterLabel || ''} onChange={(e) => updateBlock({ afterLabel: e.target.value })} style={inputStyle} />
            </div>
          </div>

          <ColorField label="Section Background" value={content.sectionColor || ''} onChange={(color) => updateBlock({ sectionColor: color })} />
        </>
      )}

      {/* Layout Price Card Editor */}
      {block.type === 'layout-price-card' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Service Name</label>
            <input
              type="text"
              value={content.serviceName || ''}
              onChange={(e) => updateBlock({ serviceName: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Description</label>
            <AutoGrowTextarea
              value={content.description || ''}
              onChange={(e) => updateBlock({ description: e.target.value })}
              style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Original Price (£)</label>
              <input type="text" value={content.originalPrice || ''} onChange={(e) => updateBlock({ originalPrice: e.target.value })} placeholder="Optional" style={inputStyle} />
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Sale Price (£)</label>
              <input type="text" value={content.salePrice || ''} onChange={(e) => updateBlock({ salePrice: e.target.value })} style={inputStyle} />
            </div>
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <ColorField label="Price Colour" value={content.priceColor || '#059669'} onChange={(color) => updateBlock({ priceColor: color })} />
          <ColorField label="Accent Colour" value={content.accentColor || '#007EA0'} onChange={(color) => updateBlock({ accentColor: color })} />
          <ColorField label="Card Background" value={content.cardBgColor || '#f9fafb'} onChange={(color) => updateBlock({ cardBgColor: color })} />
          <ColorField label="Section Background" value={content.sectionColor || ''} onChange={(color) => updateBlock({ sectionColor: color })} />
        </>
      )}

      {/* Layout Fancy Divider Editor */}
      {block.type === 'layout-fancy-divider' && (
        <ColorField
          label="Dot Colour"
          value={content.color || '#007EA0'}
          onChange={(color) => updateBlock({ color })}
        />
      )}

      {/* Layout Wave Divider Editor */}
      {block.type === 'layout-wave-divider' && (
        <>
          <ColorField
            label="Wave Colour"
            value={content.color || '#f3f4f6'}
            onChange={(color) => updateBlock({ color })}
          />
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Position</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['top', 'bottom'].map(pos => (
                <button
                  key={pos}
                  onClick={() => updateBlock({ position: pos })}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: (content.position || 'bottom') === pos ? '#29282D' : '#f3f4f6',
                    color: (content.position || 'bottom') === pos ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: pos === 'top' ? '8px 0 0 8px' : '0 8px 8px 0',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {pos}
                </button>
              ))}
            </div>
          </div>
        </>
      )}

      {/* Layout Utility Bar Editor */}
      {block.type === 'layout-utility-bar' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Left Text</label>
            <input
              type="text"
              value={content.leftText || ''}
              onChange={(e) => updateBlock({ leftText: e.target.value })}
              placeholder="e.g. A NEW DESTINATION"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Right Text (optional)</label>
            <input
              type="text"
              value={content.rightText || ''}
              onChange={(e) => updateBlock({ rightText: e.target.value })}
              placeholder="Leave empty to centre the left text"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Bottom Rule</label>
            <button
              onClick={() => updateBlock({ showRule: !content.showRule })}
              style={{
                ...inputStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: content.showRule ? '#29282D' : 'white',
                color: content.showRule ? 'white' : '#374151'
              }}
            >
              {content.showRule ? 'Rule shown' : 'No rule'}
            </button>
          </div>

          <ColorField label="Text Colour" value={content.textColor || '#6b7280'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || ''} onChange={(color) => updateBlock({ backgroundColor: color })} />
          {content.showRule && (
            <ColorField label="Rule Colour" value={content.ruleColor || '#e8e4de'} onChange={(color) => updateBlock({ ruleColor: color })} />
          )}
        </>
      )}

      {/* Layout Editorial Hero Editor */}
      {block.type === 'layout-editorial-hero' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Eyebrow (optional)</label>
            <input
              type="text"
              value={content.eyebrow || ''}
              onChange={(e) => updateBlock({ eyebrow: e.target.value })}
              placeholder="Small caps line above the headline"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Headline</label>
            <AutoGrowTextarea
              value={content.headline || ''}
              onChange={(e) => updateBlock({ headline: e.target.value })}
              placeholder="Use a line break for multi-line headlines"
              style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Subtext (optional)</label>
            <AutoGrowTextarea
              value={content.subtext || ''}
              onChange={(e) => updateBlock({ subtext: e.target.value })}
              style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', marginBottom: '16px' }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Headline Style</label>
              <select
                value={content.headlineUppercase ? 'caps' : (content.headlineItalic ? 'serif-italic' : 'serif')}
                onChange={(e) => {
                  const style = e.target.value
                  if (style === 'caps') {
                    updateBlock({ headlineFontFamily: 'Inter, system-ui, sans-serif', headlineWeight: '800', headlineUppercase: true, headlineItalic: false })
                  } else {
                    updateBlock({ headlineFontFamily: 'Georgia, serif', headlineWeight: '400', headlineUppercase: false, headlineItalic: style === 'serif-italic' })
                  }
                }}
                style={inputStyle}
              >
                <option value="serif">Serif</option>
                <option value="serif-italic">Serif Italic</option>
                <option value="caps">Bold Caps</option>
              </select>
            </div>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Headline Size</label>
              <select
                value={String(content.headlineSize || 44)}
                onChange={(e) => updateBlock({ headlineSize: Number(e.target.value) })}
                style={inputStyle}
              >
                {[36, 40, 44, 48, 52].map(size => (
                  <option key={size} value={size}>{size}px</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Alignment</label>
            <div style={{ display: 'flex', gap: '2px' }}>
              {['left', 'center'].map((align, i) => (
                <button
                  key={align}
                  onClick={() => updateBlock({ align })}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: (content.align || 'center') === align ? '#29282D' : '#f3f4f6',
                    color: (content.align || 'center') === align ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: i === 0 ? '8px 0 0 8px' : '0 8px 8px 0',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {align === 'center' ? 'centre' : align}
                </button>
              ))}
            </div>
          </div>

          <ColorField label="Text Colour" value={content.textColor || '#1a1a1a'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Eyebrow Colour" value={content.accentColor || ''} onChange={(color) => updateBlock({ accentColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || '#ffffff'} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Layout Section Intro Editor */}
      {block.type === 'layout-section-intro' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Eyebrow (optional)</label>
            <input
              type="text"
              value={content.eyebrow || ''}
              onChange={(e) => updateBlock({ eyebrow: e.target.value })}
              placeholder="e.g. SIGNATURE SERVICES"
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Heading</label>
            <input
              type="text"
              value={content.heading || ''}
              onChange={(e) => updateBlock({ heading: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Heading Style</label>
            <select
              value={content.headingWeight === '700' ? 'bold' : (content.headingItalic ? 'serif-italic' : 'serif')}
              onChange={(e) => {
                const style = e.target.value
                if (style === 'bold') {
                  updateBlock({ headingFontFamily: 'Inter, system-ui, sans-serif', headingWeight: '700', headingItalic: false })
                } else {
                  updateBlock({ headingFontFamily: 'Georgia, serif', headingWeight: '400', headingItalic: style === 'serif-italic' })
                }
              }}
              style={inputStyle}
            >
              <option value="serif">Serif</option>
              <option value="serif-italic">Serif Italic</option>
              <option value="bold">Bold Sans</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Body (optional)</label>
            <AutoGrowTextarea
              value={content.body || ''}
              onChange={(e) => updateBlock({ body: e.target.value })}
              style={{ ...inputStyle, minHeight: '70px', resize: 'vertical' }}
            />
          </div>

          <ColorField label="Text Colour" value={content.textColor || '#1a1a1a'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Eyebrow Colour" value={content.accentColor || '#9ca3af'} onChange={(color) => updateBlock({ accentColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || ''} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Layout Duo Grid Editor */}
      {block.type === 'layout-duo-grid' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Left Image</label>
            <ImageField src={content.leftImage} height={80} marginBottom={0} onPick={() => onOpenImagePicker && onOpenImagePicker('leftImage')} addLabel="Choose left image" changeLabel="Change left image" />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Left Title</label>
            <input
              type="text"
              value={content.leftTitle || ''}
              onChange={(e) => updateBlock({ leftTitle: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>Left Caption</label>
            <input
              type="text"
              value={content.leftCaption || ''}
              onChange={(e) => updateBlock({ leftCaption: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Right Image</label>
            <ImageField src={content.rightImage} height={80} marginBottom={0} onPick={() => onOpenImagePicker && onOpenImagePicker('rightImage')} addLabel="Choose right image" changeLabel="Change right image" />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Right Title</label>
            <input
              type="text"
              value={content.rightTitle || ''}
              onChange={(e) => updateBlock({ rightTitle: e.target.value })}
              style={inputStyle}
            />
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Right Caption</label>
            <input
              type="text"
              value={content.rightCaption || ''}
              onChange={(e) => updateBlock({ rightCaption: e.target.value })}
              style={inputStyle}
            />
          </div>

          <ColorField label="Text Colour" value={content.textColor || '#1a1a1a'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || ''} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Layout Shape Image Editor */}
      {block.type === 'layout-shape-image' && (
        <>
          <ImageField src={content.imageUrl} height={100} onPick={() => onOpenImagePicker && onOpenImagePicker('imageUrl')} addLabel="Choose image" changeLabel="Change image" />

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Shape</label>
            <select
              value={content.shape || 'arch'}
              onChange={(e) => updateBlock({ shape: e.target.value })}
              style={inputStyle}
            >
              <option value="arch">Arch</option>
              <option value="circle">Circle</option>
              <option value="flower">Flower</option>
              <option value="scallop">Scallop</option>
              <option value="heart">Heart</option>
              <option value="blob">Blob</option>
            </select>
          </div>

          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Width</label>
            <select
              value={String(content.width || 78)}
              onChange={(e) => updateBlock({ width: Number(e.target.value) })}
              style={inputStyle}
            >
              <option value="50">Small</option>
              <option value="66">Medium</option>
              <option value="78">Large</option>
              <option value="92">Extra Large</option>
            </select>
          </div>

          <ColorField label="Frame Colour (optional)" value={content.frameColor || ''} onChange={(color) => updateBlock({ frameColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || ''} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Layout Quote Chips Editor */}
      {block.type === 'layout-quote-chips' && (
        <>
          {['q1', 'q2', 'q3', 'q4'].map((key, idx) => (
            <div key={key} style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Quote {idx + 1}</label>
              <input
                type="text"
                value={content[key] || ''}
                onChange={(e) => updateBlock({ [key]: e.target.value })}
                placeholder='e.g. "best fade in town"'
                style={inputStyle}
              />
            </div>
          ))}

          <ColorField label="Chip Colour" value={content.chipColor || '#ffffff'} onChange={(color) => updateBlock({ chipColor: color })} />
          <ColorField label="Text Colour" value={content.textColor || '#1a1a1a'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || ''} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Footer Block Editor */}
      {block.type === 'footer' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Footer Text</label>
            <AutoGrowTextarea
              value={content.text || ''}
              onChange={(e) => updateBlock({ text: e.target.value })}
              style={{ ...inputStyle, minHeight: '100px', resize: 'vertical' }}
              placeholder="© 2024 Your Salon. All rights reserved.&#10;&#10;Unsubscribe"
            />
          </div>

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />

          <ColorField
            label="Background Colour"
            value={content.backgroundColor || '#f9fafb'}
            onChange={(color) => updateBlock({ backgroundColor: color })}
          />

          <ColorField
            label="Text Colour"
            value={content.textColor || '#9ca3af'}
            onChange={(color) => updateBlock({ textColor: color })}
          />
        </>
      )}

      {/* Testimonial Layout Editor */}
      {block.type === 'layout-testimonial' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Quote</label>
            <AutoGrowTextarea
              value={content.quote || ''}
              onChange={(e) => updateBlock({ quote: e.target.value })}
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Name</label>
            <input type="text" value={content.name || ''} onChange={(e) => updateBlock({ name: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Title</label>
            <input type="text" value={content.title || ''} onChange={(e) => updateBlock({ title: e.target.value })} style={inputStyle} />
          </div>
          <ImageField src={content.avatarUrl} height={80} onPick={() => onOpenImagePicker && onOpenImagePicker('block')} addLabel="Add photo" changeLabel="Change photo" />
          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />
          <ColorField label="Text Colour" value={content.textColor || '#374151'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || '#f9fafb'} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Stats Layout Editor */}
      {block.type === 'layout-stats' && (
        <>
          {(content.stats || []).map((stat, idx) => (
            <div key={idx} style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Value {idx + 1}</label>
                <input
                  type="text"
                  value={stat.value || ''}
                  onChange={(e) => {
                    const stats = (content.stats || []).map((s, i) => (i === idx ? { ...s, value: e.target.value } : s))
                    updateBlock({ stats })
                  }}
                  style={inputStyle}
                />
              </div>
              <div style={{ flex: 1.4 }}>
                <label style={labelStyle}>Label {idx + 1}</label>
                <input
                  type="text"
                  value={stat.label || ''}
                  onChange={(e) => {
                    const stats = (content.stats || []).map((s, i) => (i === idx ? { ...s, label: e.target.value } : s))
                    updateBlock({ stats })
                  }}
                  style={inputStyle}
                />
              </div>
            </div>
          ))}

          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />
          <ColorField label="Value Colour" value={content.textColor || '#007EA0'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Label Colour" value={content.labelColor || '#6b7280'} onChange={(color) => updateBlock({ labelColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || ''} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Contact Card Layout Editor */}
      {block.type === 'layout-contact-card' && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Address</label>
            <input type="text" value={content.address || ''} onChange={(e) => updateBlock({ address: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Phone</label>
            <input type="text" value={content.phone || ''} onChange={(e) => updateBlock({ phone: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Email</label>
            <input type="text" value={content.email || ''} onChange={(e) => updateBlock({ email: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Hours</label>
            <input type="text" value={content.hours || ''} onChange={(e) => updateBlock({ hours: e.target.value })} style={inputStyle} />
          </div>
          <ColorField label="Text Colour" value={content.textColor || '#374151'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || '#f9fafb'} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Editorial Layout Editor */}
      {block.type === 'layout-editorial' && (
        <>
          <ImageField src={content.imageUrl} height={100} onPick={() => onOpenImagePicker && onOpenImagePicker('block')} />
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Headline</label>
            <input type="text" value={content.headline || ''} onChange={(e) => updateBlock({ headline: e.target.value })} style={inputStyle} />
          </div>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Body</label>
            <AutoGrowTextarea value={content.body || ''} onChange={(e) => updateBlock({ body: e.target.value })} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} />
          </div>
          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />
          <ColorField label="Text Colour" value={content.textColor || '#1a1a1a'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || ''} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Polaroid Layout Editor */}
      {block.type === 'layout-polaroid' && (
        <>
          <ImageField src={content.imageUrl} height={100} onPick={() => onOpenImagePicker && onOpenImagePicker('block')} />
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Caption</label>
            <input type="text" value={content.caption || ''} onChange={(e) => updateBlock({ caption: e.target.value })} style={inputStyle} />
          </div>
          <FontPicker
            value={content.fontFamily || 'Inter, system-ui, sans-serif'}
            onChange={(fontFamily) => updateBlock({ fontFamily })}
          />
          <ColorField label="Caption Colour" value={content.textColor || '#1a1a1a'} onChange={(color) => updateBlock({ textColor: color })} />
          <ColorField label="Background Colour" value={content.backgroundColor || '#f9fafb'} onChange={(color) => updateBlock({ backgroundColor: color })} />
        </>
      )}

      {/* Social Strip Editor — also serves the plain 'social' block, which
          reads the same facebook/instagram/twitter/tiktok/style/iconColor props */}
      {(block.type === 'layout-social-strip' || block.type === 'social') && (
        <>
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Icon Style</label>
            <div style={{ display: 'flex', gap: '4px' }}>
              {['circle', 'rounded'].map(style => (
                <button
                  key={style}
                  onClick={() => updateBlock({ style })}
                  style={{
                    flex: 1,
                    padding: '10px',
                    backgroundColor: (content.style || 'rounded') === style ? '#29282D' : '#f3f4f6',
                    color: (content.style || 'rounded') === style ? 'white' : '#374151',
                    border: 'none',
                    borderRadius: style === 'circle' ? '8px 0 0 8px' : '0 8px 8px 0',
                    fontSize: '12px',
                    cursor: 'pointer',
                    textTransform: 'capitalize'
                  }}
                >
                  {style}
                </button>
              ))}
            </div>
          </div>

          {['facebook', 'instagram', 'twitter', 'tiktok'].map(platform => (
            <div key={platform} style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>{platform.charAt(0).toUpperCase() + platform.slice(1)}</label>
              <input
                type="text"
                value={content[platform] || ''}
                onChange={(e) => updateBlock({ [platform]: e.target.value })}
                placeholder={`https://${platform}.com/yoursalon`}
                style={inputStyle}
              />
            </div>
          ))}

          <ColorField label="Icon Colour" value={content.iconColor || '#29282D'} onChange={(color) => updateBlock({ iconColor: color })} />
        </>
      )}
    </div>
  )
}

// Shared styles
const labelStyle = {
  display: 'block',
  fontSize: '12px',
  color: '#6b7280',
  marginBottom: '6px'
}

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e5e5e5',
  borderRadius: '8px',
  fontSize: '14px',
  boxSizing: 'border-box',
  fontFamily: 'inherit'
}

const iconButtonStyle = {
  width: '28px',
  height: '28px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: 'none',
  backgroundColor: 'transparent',
  borderRadius: '6px',
  cursor: 'pointer',
  color: '#6b7280'
}

/* A real secondary button, not a dashed drop-zone: dashed borders read as
   "drag something here", and this is a tap target. */
const imagePickerButtonStyle = {
  width: '100%',
  padding: '11px 12px',
  backgroundColor: '#fff',
  border: '1px solid #D6D6DC',
  borderRadius: '10px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  color: '#29282D',
  fontSize: '13px',
  fontWeight: '600',
  fontFamily: 'inherit',
  marginBottom: '16px'
}

/* Brand teal, matching pickers elsewhere in the app — the previous minty
   green (#0d9488 on #f0fdfa) belonged to no palette Beu uses. */
const contentPickerButtonStyle = {
  width: '100%',
  padding: '12px',
  backgroundColor: 'rgba(0, 126, 160, 0.06)',
  border: '1px solid rgba(0, 126, 160, 0.35)',
  borderRadius: '10px',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  color: '#007EA0',
  fontSize: '13px',
  fontWeight: '600',
  fontFamily: 'inherit',
  marginBottom: '20px'
}
